import Phaser from "phaser";
import i18next from "i18next";
import { getSafeAreaInsetLeft } from "../../../utils/mobile-utils";
import { ItemSlot } from "../../../ui/item-slot";
import { ItemUI } from "../../../ui/item-ui";
import { SpriteLoader } from "../../../sprite-loader";
import { COLORS } from "../../../ui/colors";
import { ModernButton } from "../../../ui/modern-button";
import { BaseWindow } from "../../../ui/windows/base-window";
import { playSound } from "../../../utils/sound-utils";
import { calcSellPrice, getItemStat, Item, ItemDefinition, ItemType, EquipType, SlotType } from "../../../models/common";
import { getMaterialOutlineColor } from "../../../ui/colors";
import { fetchItemDefs } from "../../../item-catalog";

export function findAmmoDefForWeapon(weaponDefId: number, itemDefs: ItemDefinition[]): ItemDefinition | undefined {
  return itemDefs.find((def) => {
    if (def.type !== ItemType.Ammo || !def.stats?.weaponId) return false;
    const weaponIdRaw = def.stats.weaponId as unknown as number | number[];
    const weaponIds = Array.isArray(weaponIdRaw) ? weaponIdRaw : [weaponIdRaw];
    return weaponIds.includes(weaponDefId);
  });
}

export class StorageWindow extends BaseWindow {
  private showSellButton: boolean = false;
  private sellButton: ModernButton | null = null;
  private reloadButton: ModernButton | null = null;
  private itemDefsCache: ItemDefinition[] = [];
  private creditsBg: Phaser.GameObjects.Graphics | null = null;
  private creditsIcon: Phaser.GameObjects.Sprite | null = null;
  private creditsText: Phaser.GameObjects.Text | null = null;
  private showCredits: boolean = true;
  private showEquipButton: boolean = false;
  private equipButton: ModernButton | null = null;
  private showTakeButton: boolean = false;
  private takeButton: ModernButton | null = null;
  private showBackButton: boolean = false;
  private backButtonInstance: ModernButton | null = null;
  private onBackCallback: (() => void) | null = null;
  private headerButtons: Phaser.GameObjects.Container[] = [];

  public setShowSellButton(show: boolean) {
    this.showSellButton = show;
  }

  public setShowEquipButton(show: boolean) {
    this.showEquipButton = show;
  }

  public setShowCredits(show: boolean) {
    this.showCredits = show;
  }

  public setShowTakeButton(show: boolean): void {
    this.showTakeButton = show;
  }

  public setShowBackButton(show: boolean): void {
    this.showBackButton = show;
  }

  public setOnBack(callback: () => void): void {
    this.onBackCallback = callback;
  }

  public setScrollMaskOffset(x: number, y: number): void {
    this.maskOffsetX = x;
    this.maskOffsetY = y;
    if (!this.maskGraphics) return;
    const maskPad = 4;
    this.maskGraphics.clear();
    this.maskGraphics.fillStyle(0xffffff);
    this.maskGraphics.fillRect(
      this.contentStartX - maskPad + x,
      this.contentStartY - maskPad + y,
      this.contentWidth + maskPad * 2,
      this.contentHeight + maskPad * 2,
    );
  }

  private showItemTypeButtons: boolean = true;

  public setShowItemTypeButtons(show: boolean) {
    this.showItemTypeButtons = show;
  }
  private hiddenItemTypes: ItemType[] = [];

  public setHiddenItemTypes(types: ItemType[]): void {
    this.hiddenItemTypes = types;
    if (this.filteredItemType && this.hiddenItemTypes.includes(this.filteredItemType)) {
      this.filteredItemType = null;
    }
  }
  private filteredItemType: ItemType | null = null;
  // Virtualized grid: only items within (roughly) the visible viewport are ever
  // mounted as real GameObjects, keyed by their index into itemsToDisplay. This
  // keeps rendering/scroll cost bounded by viewport size, not total item count,
  // which matters because storage size is unbounded and scales with playtime.
  private itemsToDisplay: Item[] = [];
  private gridCols: number = 5;
  private gridLayer: Phaser.GameObjects.Container | null = null;
  private mountedSlots: Map<number, ItemSlot> = new Map();
  private mountedSprites: Map<number, Phaser.GameObjects.GameObject[]> = new Map();
  private readonly SLOT_PADDING_X: number = 8;
  private readonly SLOT_PADDING_Y: number = 8;
  private readonly VISIBLE_ROW_BUFFER: number = 1;
  private scrollRafId: number | null = null;
  private selectedItemIdx: number | null = null;
  // private actionButton: Phaser.GameObjects.Container | null = null;
  private scrollContainer: Phaser.GameObjects.Container | null = null;
  private maskGraphics: Phaser.GameObjects.Graphics | null = null;
  private scrollY: number = 0;
  private maxScrollY: number = 0;
  private isDragging: boolean = false;
  private dragStartY: number = 0;
  private dragStartScrollY: number = 0;
  private pointerMoveHandler: ((pointer: Phaser.Input.Pointer) => void) | null = null;
  private pointerUpHandler: ((pointer: Phaser.Input.Pointer) => void) | null = null;
  private pointerDownHandler: ((pointer: Phaser.Input.Pointer) => void) | null = null;
  private wheelHandler: ((event: WheelEvent) => void) | null = null;
  private geometryMask: Phaser.Display.Masks.GeometryMask | null = null;
  private scrollbarGraphics: Phaser.GameObjects.Graphics | null = null;
  private filteredEquipType: EquipType | null = null;
  private allItems: Item[] = [];
  private noItemsText: Phaser.GameObjects.Text | null = null;
  private contentStartX: number = 0;
  private contentStartY: number = 0;
  private contentWidth: number = 0;
  private contentHeight: number = 0;
  private maskOffsetX: number = 0;
  private maskOffsetY: number = 0;
  private slotSize: number = 48;
  private windowX: number = 0;
  private windowY: number = 0;
  private windowWidth: number = 0;
  private windowHeight: number = 0;
  private layoutMode: "compact" | "fullscreen" = "compact";
  private totalRows: number = 0;
  private readonly SCROLLBAR_ROW_THRESHOLD: number = 3;

  constructor(scene: Phaser.Scene) {
    super(scene);
    fetchItemDefs().then((defs) => {
      this.itemDefsCache = defs;
    });
  }

  public setLayoutMode(mode: "compact" | "fullscreen"): void {
    this.layoutMode = mode;
  }

  protected getLayoutOverrides(): Partial<{
    x: number;
    y: number;
    width: number;
    height: number;
  }> {
    if (this.layoutMode === "fullscreen") {
      // Full-screen mode: fill whole screen with margins
      const gameWidth = this.scene.scale.width;
      const gameHeight = this.scene.scale.height;
      const marginRight = 32;
      const marginLeft = 120 + getSafeAreaInsetLeft(); // Extra left margin for side buttons + Dynamic Island
      const marginY = 32;
      const navPadding = this.scene.scene.key === "BaseScene" ? 88 : 0;

      return {
        x: marginLeft,
        y: 40,
        width: gameWidth - marginLeft - marginRight,
        height: gameHeight - 40 - marginY - navPadding,
      };
    } else {
      // Compact mode: fixed small size for raider screen
      return { height: 240, width: 372 };
    }
  }

  public setFilteredEquipType(equipType: EquipType | null): void {
    this.filteredEquipType = equipType;
    this.updateGridDisplay();
  }

  // Public method to refresh grid display with latest storage data
  public refresh(): void {
    // Always re-show to update all slots and UI
    this.show(true);
  }

  public updateFilteredEquipType(equipType: EquipType | null): void {
    // Update filter and refresh display without closing window
    this.filteredEquipType = equipType;
    this.selectedItemIdx = null;
    // Removed action button functionality
    
    // Update the title to show the new equip type
    if (this.container) {
      const children = this.container.getAll();
      // Find the title text (should be the first Text object in the container)
      for (const child of children) {
        if (child instanceof Phaser.GameObjects.Text) {
          const newTitle = this.filteredEquipType
            ? this.filteredEquipType
            : i18next.t("ui:storage");
          child.setText(newTitle);
          break;
        }
      }
    }
    
    this.updateGridDisplay();
  }

  // Removed action button functionality

  // Removed showActionButton and related logic

  private getSlotPosition(idx: number): { x: number; y: number } {
    const row = Math.floor(idx / this.gridCols);
    const col = idx % this.gridCols;
    return {
      x: this.contentStartX + col * (this.slotSize + this.SLOT_PADDING_X),
      y: this.contentStartY + row * (this.slotSize + this.SLOT_PADDING_Y),
    };
  }

  /** Positions/toggles visibility of one already-mounted slot for the current scrollY. */
  private applySlotScrollY(idx: number): void {
    const slot = this.mountedSlots.get(idx);
    if (!slot?.rect) return;
    // Keep this buffer within the mask's clip bounds (see maskPad in show()) so a
    // scrolled-out-of-view slot never stays interactive over the header/tab row above it.
    const visibilityBufferY = 4;
    const visibleTop = this.contentStartY - visibilityBufferY;
    const visibleBottom = this.contentStartY + this.contentHeight + visibilityBufferY;

    const { y: originalY } = this.getSlotPosition(idx);
    const newY = originalY - this.scrollY;
    slot.rect.setY(newY);
    const isVisible = newY + this.slotSize > visibleTop && newY < visibleBottom;
    slot.rect.setVisible(isVisible);

    const sprites = this.mountedSprites.get(idx);
    if (sprites) {
      sprites.forEach((sprite) => {
        const originalSpriteY = (sprite as any).originalY;
        if (originalSpriteY !== undefined) {
          (sprite as any).setY(originalSpriteY - this.scrollY);
        }
        (sprite as any).setVisible(isVisible);
      });
    }
  }

  /** Creates the GameObjects for one item's slot and adds them to the (masked) grid layer. */
  private mountSlotAt(idx: number): void {
    if (this.mountedSlots.has(idx)) return;
    const item = this.itemsToDisplay[idx];
    if (!item) return;

    const { x: slotX, y: slotY } = this.getSlotPosition(idx);

    const itemSlot = new ItemSlot(slotX, slotY, true);
    itemSlot.setSelected(this.selectedItemIdx === idx);
    if (item.definition.type === ItemType.Material) {
      itemSlot.setMaterialOutlineColor(getMaterialOutlineColor(item.definition.price));
    }
    if (item.definition.type === ItemType.Recipe) {
      itemSlot.setBlueprintMode(true);
    }
    itemSlot.createRectangle(this.scene);

    if (itemSlot.rect) {
      itemSlot.rect.on("pointerdown", () => this.onSlotClicked(idx));
      this.gridLayer?.add(itemSlot.rect);
    }

    const currentSlotSprites: Phaser.GameObjects.GameObject[] = [];

    const spriteSheet = item.definition.spriteSheet || "items";
    const sprite = SpriteLoader.createSprite(
      this.scene,
      spriteSheet,
      item.definition.spriteX,
      item.definition.spriteY,
      slotX + this.slotSize / 2,
      slotY + this.slotSize / 2,
      true,
    );
    sprite.displayWidth = this.slotSize * 0.85;
    sprite.displayHeight = this.slotSize * 0.85;
    sprite.setScrollFactor(0);
    (sprite as any).originalY = slotY + this.slotSize / 2;
    currentSlotSprites.push(sprite);

    const ammoIndicator = ItemUI.renderAmmoIndicator(
      this.scene,
      item,
      slotX,
      slotY,
      this.slotSize,
    );
    if (ammoIndicator) {
      ammoIndicator.setScrollFactor(0);
      (ammoIndicator as any).originalY = slotY + this.slotSize / 2;
      currentSlotSprites.push(ammoIndicator as any);
    }

    const countBadge = ItemUI.renderCountBadge(this.scene, item, slotX, slotY, this.slotSize);
    if (countBadge) {
      countBadge.forEach((obj) => {
        (obj as any).originalY = (obj as any).y;
      });
      currentSlotSprites.push(...(countBadge as any[]));
    }

    currentSlotSprites.forEach((s) => this.gridLayer?.add(s as any));

    this.mountedSlots.set(idx, itemSlot);
    this.mountedSprites.set(idx, currentSlotSprites);

    this.applySlotScrollY(idx);
  }

  /** Destroys one item's slot GameObjects and drops it from the mounted set. */
  private unmountSlotAt(idx: number): void {
    const slot = this.mountedSlots.get(idx);
    if (slot?.rect) this.gridLayer?.remove(slot.rect, true);
    this.mountedSlots.delete(idx);

    const sprites = this.mountedSprites.get(idx);
    if (sprites) sprites.forEach((s) => this.gridLayer?.remove(s as any, true));
    this.mountedSprites.delete(idx);
  }

  /**
   * Mounts/unmounts slots so only the (roughly) visible index range has live GameObjects.
   * This is the perf-critical path: it must stay O(visible items), not O(total items),
   * since storage can grow to hundreds/thousands of items for long-time players.
   */
  private applyScrollOffset(): void {
    if (!this.scrollContainer || this.itemsToDisplay.length === 0) {
      this.updateScrollbar();
      return;
    }

    const rowHeight = this.slotSize + this.SLOT_PADDING_Y;
    const firstVisibleRow = Math.max(0, Math.floor(this.scrollY / rowHeight) - this.VISIBLE_ROW_BUFFER);
    const lastVisibleRow = Math.ceil((this.scrollY + this.contentHeight) / rowHeight) + this.VISIBLE_ROW_BUFFER;

    const firstIdx = firstVisibleRow * this.gridCols;
    const lastIdx = Math.min(
      this.itemsToDisplay.length - 1,
      (lastVisibleRow + 1) * this.gridCols - 1,
    );

    for (const idx of Array.from(this.mountedSlots.keys())) {
      if (idx < firstIdx || idx > lastIdx) this.unmountSlotAt(idx);
    }
    for (let idx = firstIdx; idx <= lastIdx; idx++) {
      if (this.mountedSlots.has(idx)) {
        this.applySlotScrollY(idx);
      } else {
        this.mountSlotAt(idx);
      }
    }

    this.updateScrollbar();
  }

  private updateScrollbar(): void {
    if (!this.scrollbarGraphics) return;
    this.scrollbarGraphics.clear();
    if (this.maxScrollY <= 0 || this.totalRows <= this.SCROLLBAR_ROW_THRESHOLD) return;

    const SCROLLBAR_WIDTH = 5;
    const scrollbarX = this.contentStartX + this.contentWidth + 4;
    const totalContentHeight = this.contentHeight + this.maxScrollY;
    const thumbHeight = Math.max(20, (this.contentHeight / totalContentHeight) * this.contentHeight);
    const thumbY = this.contentStartY + (this.scrollY / this.maxScrollY) * (this.contentHeight - thumbHeight);

    // Track
    this.scrollbarGraphics.fillStyle(0x1a3550, 0.8);
    this.scrollbarGraphics.fillRoundedRect(scrollbarX, this.contentStartY, SCROLLBAR_WIDTH, this.contentHeight, 2);

    // Thumb
    this.scrollbarGraphics.fillStyle(0x5dd9e8, 0.7);
    this.scrollbarGraphics.fillRoundedRect(scrollbarX, thumbY, SCROLLBAR_WIDTH, thumbHeight, 2);
  }

  private updateGridDisplay(): void {
    if (!this.scrollContainer) return;

    // Filter/list changed — previously mounted indices no longer refer to the right items.
    for (const idx of Array.from(this.mountedSlots.keys())) {
      this.unmountSlotAt(idx);
    }

    // Remove no items text if it exists
    if (this.noItemsText) {
      this.noItemsText.destroy();
      this.noItemsText = null;
    }

    // Filter items based on selected item type or equip type
    let itemsToDisplay = this.hiddenItemTypes.length > 0
      ? this.allItems.filter((item) => !this.hiddenItemTypes.includes(item.definition.type))
      : this.allItems;
    if (this.filteredItemType) {
      itemsToDisplay = itemsToDisplay.filter((item) => item.definition.type === this.filteredItemType);
    } else if (this.filteredEquipType) {
      itemsToDisplay = itemsToDisplay.filter((item) =>
        item.definition.type === ItemType.Equipment &&
        item.definition.equipType === this.filteredEquipType
      );
    }
    this.itemsToDisplay = itemsToDisplay;

    // Show "No items" if empty
    if (itemsToDisplay.length === 0) {
      this.totalRows = 0;
      this.noItemsText = this.scene.add.text(
        this.contentStartX + this.contentWidth / 2,
        this.contentStartY + this.contentHeight / 2,
        "No items",
        {
          fontSize: "16px",
          color: COLORS.WHITE,
          fontFamily: "Anton",
          align: "center",
        },
      );
      this.noItemsText.setOrigin(0.5, 0.5);
      this.noItemsText.setScrollFactor(0);
      this.container?.add(this.noItemsText);
      this.scrollY = 0;
      this.maxScrollY = 0;
      this.updateScrollbar();
      return;
    }

    // Grid layout
    this.gridCols = this.layoutMode === "fullscreen"
      ? Math.max(1, Math.floor((this.contentWidth + this.SLOT_PADDING_X) / (this.slotSize + this.SLOT_PADDING_X)))
      : 5;
    this.totalRows = Math.ceil(itemsToDisplay.length / this.gridCols);
    const gridHeight =
      this.totalRows > 0
        ? this.totalRows * (this.slotSize + this.SLOT_PADDING_Y) - this.SLOT_PADDING_Y
        : 0;

    this.maxScrollY = Math.max(0, gridHeight - this.contentHeight);
    this.scrollY = 0;

    // Remove previous sell button if it exists
    if (this.sellButton) {
      this.sellButton.destroy();
      this.sellButton = null;
    }
    if (this.reloadButton) {
      this.reloadButton.destroy();
      this.reloadButton = null;
    }
    // Remove previous equip button if it exists
    if (this.equipButton) {
      this.equipButton.destroy();
      this.equipButton = null;
    }
    if (this.takeButton) {
      this.takeButton.destroy();
      this.takeButton = null;
    }
    if (this.backButtonInstance) {
      this.backButtonInstance.destroy();
      this.backButtonInstance = null;
    }
    if (this.showBackButton) {
      this.backButtonInstance = new ModernButton({
        scene: this.scene,
        x: this.windowX + this.windowWidth / 2,
        y: this.windowY + this.windowHeight,
        text: "BACK",
        style: "secondary",
        size: "normal",
        onClick: () => {
          if (this.onBackCallback) this.onBackCallback();
        },
      });
      this.container?.add(this.backButtonInstance);
    }

    // Only slots within (roughly) the visible viewport get mounted as GameObjects here —
    // see applyScrollOffset/mountSlotAt, which keep cost bounded by viewport size rather
    // than total item count.
    this.applyScrollOffset();
  }

  /** Click handler for a slot's rect — looks up the item fresh so it stays correct across remounts. */
  private onSlotClicked(idx: number): void {
    const item = this.itemsToDisplay[idx];
    if (!item) return;
    playSound(this.scene, "menu-click-sound", null, 0.5);
    this.selectedItemIdx = idx;
    this.mountedSlots.forEach((s, slotIdx) => {
      s.setSelected(slotIdx === idx);
      s.applyBorderStyle();
    });
    // Determine whether this weapon can be reloaded for credits
    const maxAmmo = getItemStat(item, "ammo");
    const isWeapon = item.definition.type === ItemType.Equipment && item.definition.equipType === EquipType.Weapon;
    const currentAmmo = item.currentAmmo ?? maxAmmo;
    let ammoDef: ItemDefinition | undefined;
    let reloadCost = 0;
    if (isWeapon && maxAmmo > 0 && currentAmmo < maxAmmo) {
      ammoDef = findAmmoDefForWeapon(item.definition.id, this.itemDefsCache);
      if (ammoDef) {
        reloadCost = Math.ceil(ammoDef.price * (1 - currentAmmo / maxAmmo));
      }
    }
    const isMinigun = item.definition.id === 30;
    const showReload = this.showSellButton && !!ammoDef && !isMinigun;

    // Show SELL button if in storage-screen context
    if (this.showSellButton) {
      if (this.sellButton) {
        this.sellButton.destroy();
        this.sellButton = null;
      }
      const sellPrice = calcSellPrice(item.definition?.price ?? 0);
      this.sellButton = new ModernButton({
        scene: this.scene,
        x: this.windowX + this.windowWidth / 2,
        y: this.windowY + this.windowHeight,
        text: `${i18next.t("ui:buttons.sell")} ${sellPrice} c`,
        style: "success",
        size: "normal",
        onClick: () => {
          const selectedItem = this.selectedItemIdx !== null ? this.itemsToDisplay[this.selectedItemIdx] : null;
          if (selectedItem && (window as any).socket) {
            (window as any).socket.emit("sell-item-storage", { itemId: selectedItem.id });
            this.selectedItemIdx = null;
            if (this.sellButton) {
              this.container?.remove(this.sellButton, true);
              this.sellButton = null;
            }
            if (this.reloadButton) {
              this.container?.remove(this.reloadButton, true);
              this.reloadButton = null;
            }
            this.updateGridDisplay();
          }
        },
      });
      if (this.container) {
        this.container.add(this.sellButton);
      } else {
        this.scene.add.existing(this.sellButton);
      }
    }

    // Show RELOAD button if the selected weapon isn't at full ammo
    if (showReload) {
      if (this.reloadButton) {
        this.reloadButton.destroy();
        this.reloadButton = null;
      }
      this.reloadButton = new ModernButton({
        scene: this.scene,
        x: this.windowX + this.windowWidth / 2,
        y: this.windowY + this.windowHeight,
        text: `RELOAD ${reloadCost} c`,
        style: "warning",
        size: "normal",
        onClick: () => {
          const selectedItem = this.selectedItemIdx !== null ? this.itemsToDisplay[this.selectedItemIdx] : null;
          if (selectedItem && (window as any).socket) {
            (window as any).socket.emit("reload-weapon-storage", { itemId: selectedItem.id });
            this.selectedItemIdx = null;
            if (this.reloadButton) {
              this.container?.remove(this.reloadButton, true);
              this.reloadButton = null;
            }
            if (this.sellButton) {
              this.container?.remove(this.sellButton, true);
              this.sellButton = null;
            }
            this.updateGridDisplay();
          }
        },
      });
      if (this.container) {
        this.container.add(this.reloadButton);
      } else {
        this.scene.add.existing(this.reloadButton);
      }
    }

    // Lay out SELL + RELOAD side by side with an 8px gap when both are shown
    if (this.sellButton && this.reloadButton) {
      const gap = 8;
      const totalWidth = this.sellButton.buttonWidth + gap + this.reloadButton.buttonWidth;
      const centerX = this.windowX + this.windowWidth / 2;
      const buttonY = this.windowY + this.windowHeight;
      this.sellButton.setPosition(centerX - totalWidth / 2 + this.sellButton.buttonWidth / 2, buttonY);
      this.reloadButton.setPosition(centerX + totalWidth / 2 - this.reloadButton.buttonWidth / 2, buttonY);
    }
    // Show TAKE button if in inventory mode
    if (this.showTakeButton) {
      if (this.takeButton) {
        this.container?.remove(this.takeButton, true);
        this.takeButton = null;
      }
      this.takeButton = new ModernButton({
        scene: this.scene,
        x: this.showBackButton
          ? this.windowX + this.windowWidth / 2 + 52
          : this.windowX + this.windowWidth / 2,
        y: this.windowY + this.windowHeight,
        text: "TAKE",
        style: "success",
        size: "normal",
        onClick: () => {
          const selectedItem = this.selectedItemIdx !== null ? this.itemsToDisplay[this.selectedItemIdx] : null;
          if (!selectedItem) return;
          const actions: Item[] = (window as any).player?.data?.actions || [];
          const inventory: Item[] = (window as any).player?.data?.inventory || [];
          const hasBackpack = !!(window as any).player?.data?.equipment?.Back;
          const maxInventory = hasBackpack ? 8 : 0;
          if (actions.length >= 3 && (!hasBackpack || inventory.length >= maxInventory)) {
            this.showNotification("Inventory full!");
            return;
          }
          playSound(this.scene, "pick-item-sound", null, 0.5);
          if ((window as any).socket) {
            (window as any).socket.emit("take-out-item", { item: selectedItem });
          }
          this.selectedItemIdx = null;
          if (this.takeButton) {
            this.container?.remove(this.takeButton, true);
            this.takeButton = null;
          }
          this.updateGridDisplay();
        },
      });
      if (this.container) {
        this.container.add(this.takeButton);
      }
      if (this.showBackButton && this.backButtonInstance) {
        this.backButtonInstance.setPosition(
          this.windowX + this.windowWidth / 2 - 52,
          this.windowY + this.windowHeight,
        );
      }
    }
    // Show EQUIP button if in equip mode
    if (this.showEquipButton && this.filteredEquipType) {
      if (this.equipButton) {
        this.equipButton.destroy();
        this.equipButton = null;
      }
      this.equipButton = new ModernButton({
        scene: this.scene,
        x: this.showBackButton
          ? this.windowX + this.windowWidth / 2 + 52
          : this.windowX + this.windowWidth / 2,
        y: this.windowY + this.windowHeight,
        text: i18next.t("ui:buttons.equip", "Equip"),
        style: "success",
        size: "normal",
        onClick: () => {
          const selectedItem = this.selectedItemIdx !== null ? this.itemsToDisplay[this.selectedItemIdx] : null;
          if (selectedItem && (window as any).socket) {
            (window as any).socket.emit("equip-item-storage", {
              itemId: selectedItem.id,
              equipType: this.filteredEquipType,
            });
            this.selectedItemIdx = null;
            if (this.equipButton) {
              this.container?.remove(this.equipButton, true);
              this.equipButton = null;
            }
            this.updateGridDisplay();
          }
        },
      });
      if (this.container) {
        this.container.add(this.equipButton);
      } else {
        this.scene.add.existing(this.equipButton);
      }
      if (this.showBackButton && this.backButtonInstance) {
        this.backButtonInstance.setPosition(
          this.windowX + this.windowWidth / 2 - 52,
          this.windowY + this.windowHeight,
        );
      }
    }
  }

  show(preserveFilter: boolean = false) {
    // Save current filter and mask offsets before hide clears them
    const savedFilter = preserveFilter ? this.filteredEquipType : null;
    const savedMaskOffsetX = preserveFilter ? this.maskOffsetX : 0;
    const savedMaskOffsetY = preserveFilter ? this.maskOffsetY : 0;

    this.hide();
    this.selectedItemIdx = null;
    // Remove equip button if present
    if (this.equipButton) {
      this.equipButton.destroy();
      this.equipButton = null;
    }
    if (this.reloadButton) {
      this.reloadButton.destroy();
      this.reloadButton = null;
    }
    if (this.takeButton) {
      this.takeButton.destroy();
      this.takeButton = null;
    }
    if (this.backButtonInstance) {
      this.backButtonInstance.destroy();
      this.backButtonInstance = null;
    }
    this.filteredEquipType = savedFilter;
    this.maskOffsetX = savedMaskOffsetX;
    this.maskOffsetY = savedMaskOffsetY;
    if (!this.showItemTypeButtons) {
      this.filteredItemType = null;
    }
    this.scrollY = 0;

    // Use equip type as title if filtered, otherwise use "STORAGE"
    const windowLabel = this.filteredEquipType
      ? this.filteredEquipType
      : i18next.t("ui:storage");
    const baseElements = this.createBaseWindow({
      title: windowLabel,
      side: "left",
    });

    const bounds = this.getWindowBounds()!;
    const { x, y, width, height } = bounds;

    // Store window bounds for use in other methods
    this.windowX = x;
    this.windowY = y;
    this.windowWidth = width;
    this.windowHeight = height;

    // Get all items from storage

    this.allItems = [];
    if (window.player && Array.isArray(window.player.data.storage)) {
      this.allItems = window.player.data.storage as unknown as Item[];
    }

    // ── HEADER: Unique Item Type Buttons ──
    let elements: Phaser.GameObjects.GameObject[] = [...baseElements];

    // Grid layer holds all slot GameObjects; adding it here (before header/credits/back
    // button elements) keeps it below them in both render and input hit-test order,
    // regardless of when individual slots mount/unmount during virtualization.
    this.gridLayer = this.scene.add.container(0, 0);
    this.gridLayer.setScrollFactor(0);
    elements.push(this.gridLayer);

    this.headerButtons = [];
    if (this.showItemTypeButtons) {
      // Find unique item types in storage
      const uniqueTypes: ItemType[] = Array.from(
        new Set(
          this.allItems
            .map((item) => item.definition.type)
            .filter((type) => !this.hiddenItemTypes.includes(type)),
        )
      );
      // Auto-select the first item type if not already filtered
      if (!this.filteredItemType && uniqueTypes.length > 0) {
        this.filteredItemType = uniqueTypes[0];
      }
      // Place buttons to the right of the title
      const headerY = y + 24;
      let headerX = x + 160; // Offset to the right of the title (adjust as needed)
      const headerButtons: Phaser.GameObjects.Container[] = [];
      const TYPE_ICON_FRAMES: Partial<Record<ItemType, number>> = {
        [ItemType.Equipment]: 0,
        [ItemType.Consumable]: 1,
        [ItemType.Material]: 3,
        [ItemType.Throwable]: 2,
        [ItemType.Ammo]: 8,
        [ItemType.Recipe]: 9,
      };
      uniqueTypes.forEach((type, idx) => {
        const iconFrame = TYPE_ICON_FRAMES[type];
        const useIcon = iconFrame !== undefined;
        const btn = new ModernButton({
          scene: this.scene,
          x: Math.round(headerX),
          y: headerY,
          text: useIcon ? "" : String(type),
          iconOnly: useIcon,
          icon: useIcon ? "item-type-icons" : undefined,
          iconFrame: useIcon ? iconFrame : undefined,
          style: this.filteredItemType === type ? "success" : "secondary",
          size: "small",
          useSmallLabel: !useIcon,
          onClick: () => {
            if (this.filteredItemType === type) {
              this.filteredItemType = null;
            } else {
              this.filteredItemType = type;
            }
            const savedX = this.container?.x;
            const savedY = this.container?.y;
            this.show(true);
            if (this.container && savedX !== undefined && savedY !== undefined) {
              this.container.setPosition(savedX, savedY);
            }
          },
        });
        headerButtons.push(btn);
        this.scene.add.existing(btn);
        if (!useIcon && btn.buttonWidth < 80) {
          btn.buttonWidth = 80;
        }
        headerX += btn.buttonWidth + 8;
      });
      elements.push(...headerButtons);
      this.headerButtons = headerButtons;
    }

    // ── HEADER: Player credits (right-aligned, styled like the raider screen) ──
    if (this.showCredits) {
      const credits = window.player?.data?.credits || 0;
      const headerY = y + 24;

      this.creditsText = this.scene.add.text(0, headerY, `${credits} Credits`, {
        fontSize: "18px",
        color: COLORS.YELLOW,
        fontFamily: "Anton",
        align: "left",
        shadow: {
          offsetX: 0,
          offsetY: 3,
          color: "#000000",
          blur: 0,
          stroke: true,
          fill: true,
        },
      });
      this.creditsText.setOrigin(0, 0.5);
      this.creditsText.setScrollFactor(0);

      const iconW = Math.round(16 * 1.2);
      const iconTextGap = 6;
      const rowWidth = iconW + iconTextGap + this.creditsText.width;
      const rowRight = x + width - this.CONTENT_PADDING_X;
      const rowLeft = rowRight - rowWidth;

      this.creditsIcon = this.scene.add.sprite(rowLeft + iconW / 2, headerY, "items", 202);
      this.creditsIcon.setScale(1.2);
      this.creditsIcon.setScrollFactor(0);

      this.creditsText.setX(rowLeft + iconW + iconTextGap);

      const creditsBgPadX = 12;
      const creditsBgPadY = 8;
      const creditsBgHeight = Math.max(iconW, this.creditsText.height) + creditsBgPadY * 2;
      this.creditsBg = this.scene.add.graphics();
      this.creditsBg.fillStyle(0x000000, 0.55);
      this.creditsBg.fillRoundedRect(
        rowLeft - creditsBgPadX,
        headerY - creditsBgHeight / 2,
        rowWidth + creditsBgPadX * 2,
        creditsBgHeight,
        10,
      );
      this.creditsBg.setScrollFactor(0);

      elements.push(this.creditsBg, this.creditsIcon, this.creditsText);
    }

    // ── FULL WIDTH: Scrollable grid ──────────────────────────────────────
    this.contentStartX = x + this.CONTENT_PADDING_X;
    this.contentStartY = y + this.CONTENT_PADDING_Y + 20;
    this.contentWidth = width - this.CONTENT_PADDING_X * 2;
    const availableContentHeight = y + height - 12 - this.contentStartY;
    this.contentHeight = Math.max(0, availableContentHeight);

    // Clipping mask — not added to display list so it never renders visually
    const maskPad = 4;
    this.maskGraphics = this.scene.make.graphics({ add: false });
    this.maskGraphics.fillStyle(0xffffff);
    this.maskGraphics.fillRect(
      this.contentStartX - maskPad + this.maskOffsetX,
      this.contentStartY - maskPad + this.maskOffsetY,
      this.contentWidth + maskPad * 2,
      this.contentHeight + maskPad * 2,
    );
    this.geometryMask = this.maskGraphics.createGeometryMask();
    // Mask the whole grid layer once, instead of every individual slot/sprite GameObject.
    this.gridLayer?.setMask(this.geometryMask);

    // scrollContainer is a lightweight sentinel so updateGridDisplay() knows the window is open
    this.scrollContainer = this.scene.add.container(this.contentStartX, this.contentStartY);
    this.scrollContainer.setScrollFactor(0);
    elements.push(this.scrollContainer);

    // Scrollbar (drawn on top, outside the mask)
    this.scrollbarGraphics = this.scene.add.graphics();
    this.scrollbarGraphics.setScrollFactor(0);
    elements.push(this.scrollbarGraphics);

    // Mouse wheel scroll
    const canvas = this.scene.game.canvas;
    this.wheelHandler = (event: WheelEvent) => {
      if (this.maxScrollY <= 0) return;
      const containerX = this.container?.x ?? 0;
      const containerY = this.container?.y ?? 0;
      const rect = canvas.getBoundingClientRect();
      const scaleX = this.scene.scale.width / rect.width;
      const scaleY = this.scene.scale.height / rect.height;
      const canvasX = (event.clientX - rect.left) * scaleX;
      const canvasY = (event.clientY - rect.top) * scaleY;
      const hitArea = new Phaser.Geom.Rectangle(
        this.contentStartX + containerX, this.contentStartY + containerY,
        this.contentWidth + 12, this.contentHeight,
      );
      if (Phaser.Geom.Rectangle.Contains(hitArea, canvasX, canvasY)) {
        event.preventDefault();
        this.scrollY = Phaser.Math.Clamp(this.scrollY + event.deltaY * 0.5, 0, this.maxScrollY);
        if (!this.scrollRafId) {
          this.scrollRafId = requestAnimationFrame(() => {
            this.scrollRafId = null;
            this.applyScrollOffset();
          });
        }
      }
    };
    canvas.addEventListener("wheel", this.wheelHandler, { passive: false });

    const pointerDownHandler = (pointer: Phaser.Input.Pointer) => {
      const containerX = this.container?.x ?? 0;
      const containerY = this.container?.y ?? 0;
      const rect = new Phaser.Geom.Rectangle(
        this.contentStartX + containerX,
        this.contentStartY + containerY,
        this.contentWidth,
        this.contentHeight,
      );
      if (Phaser.Geom.Rectangle.Contains(rect, pointer.x, pointer.y)) {
        this.isDragging = true;
        this.dragStartY = pointer.y;
        this.dragStartScrollY = this.scrollY;
      }
    };

    const pointerMoveHandlerLocal = (pointer: Phaser.Input.Pointer) => {
      if (this.isDragging) {
        const deltaY = pointer.y - this.dragStartY;
        this.scrollY = Phaser.Math.Clamp(
          this.dragStartScrollY - deltaY,
          0,
          this.maxScrollY,
        );
        if (!this.scrollRafId) {
          this.scrollRafId = requestAnimationFrame(() => {
            this.scrollRafId = null;
            this.applyScrollOffset();
          });
        }
      }
    };

    const pointerUpHandlerLocal = () => {
      this.isDragging = false;
    };

    this.pointerDownHandler = pointerDownHandler;
    this.scene.input.on("pointerdown", pointerDownHandler);
    this.pointerMoveHandler = pointerMoveHandlerLocal;
    this.pointerUpHandler = pointerUpHandlerLocal;
    this.scene.input.on("pointermove", pointerMoveHandlerLocal);
    this.scene.input.on("pointerup", pointerUpHandlerLocal);

    this.container = this.scene.add.container(0, 0, elements);
    this.container.setDepth(100200);
    this.visible = true;
    this.addContainerToParent(this.container);

    // Display all items initially
    this.updateGridDisplay();
  }

  hide() {
    if (this.pointerDownHandler) {
      this.scene.input.off("pointerdown", this.pointerDownHandler);
      this.pointerDownHandler = null;
    }
    if (this.pointerMoveHandler) {
      this.scene.input.off("pointermove", this.pointerMoveHandler);
      this.pointerMoveHandler = null;
    }
    if (this.pointerUpHandler) {
      this.scene.input.off("pointerup", this.pointerUpHandler);
      this.pointerUpHandler = null;
    }
    if (this.wheelHandler) {
      this.scene.game.canvas.removeEventListener("wheel", this.wheelHandler);
      this.wheelHandler = null;
    }
    this.geometryMask = null;
    if (this.maskGraphics) {
      this.maskGraphics.destroy();
      this.maskGraphics = null;
    }
    this.scrollbarGraphics = null;
    this.maskOffsetX = 0;
    this.maskOffsetY = 0;

    if (this.creditsBg) {
      this.creditsBg.destroy();
      this.creditsBg = null;
    }
    if (this.creditsIcon) {
      this.creditsIcon.destroy();
      this.creditsIcon = null;
    }
    if (this.creditsText) {
      this.creditsText.destroy();
      this.creditsText = null;
    }

    this.selectedItemIdx = null;
    if (this.takeButton) {
      this.takeButton.destroy();
      this.takeButton = null;
    }
    if (this.sellButton) {
      this.sellButton.destroy();
      this.sellButton = null;
    }
    if (this.reloadButton) {
      this.reloadButton.destroy();
      this.reloadButton = null;
    }
    if (this.backButtonInstance) {
      this.backButtonInstance.destroy();
      this.backButtonInstance = null;
    }
    this.filteredEquipType = null;
    this.scrollContainer = null;
    this.scrollY = 0;
    this.maxScrollY = 0;
    this.totalRows = 0;
    if (this.scrollRafId !== null) {
      cancelAnimationFrame(this.scrollRafId);
      this.scrollRafId = null;
    }
    // The container (destroyed below by super.hide()) owns gridLayer and every mounted
    // slot/sprite, so we only need to clear our bookkeeping, not destroy them individually.
    this.mountedSlots.clear();
    this.mountedSprites.clear();
    this.gridLayer = null;
    this.itemsToDisplay = [];
    this.allItems = [];
    if (this.noItemsText) {
      this.noItemsText.destroy();
      this.noItemsText = null;
    }

    super.hide();
  }

  private showNotification(msg: string): void {
    const notif = this.scene.add.text(
      this.windowX + this.windowWidth / 2,
      this.windowY + this.windowHeight / 2,
      msg,
      {
        fontSize: "18px",
        color: "#ff4444",
        fontFamily: "Anton",
        align: "center",
        backgroundColor: "#00000099",
        padding: { x: 12, y: 6 },
      },
    );
    notif.setOrigin(0.5, 0.5);
    notif.setScrollFactor(0);
    notif.setDepth(200001);
    this.container?.add(notif);
    this.scene.time.delayedCall(2000, () => {
      try { notif.destroy(); } catch (_) {}
    });
  }

  getCurrentRoomName(): string | null {
    return null;
  }
}
